import { useState } from 'react'
import { Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { InstanceUsage } from '@/api/types'
import { SystemHealth } from '@/features/admin/components/system-health'
import { useEmailTest, useHealth } from '@/features/admin/queries'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Skeleton } from '@/shared/ui/skeleton'

/** What the backend answers for a check that is fine. */
const WORKING = 'working'

const COUNT = new Intl.NumberFormat()
const COUNTED = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

/**
 * Whether the things this instance depends on are answering, and how hard the
 * host and its workers are working.
 *
 * The checks are read at the moment somebody suspects something is wrong, which
 * is why that query is never cached and why a failing check shows what the
 * server said rather than a word of its own: the message is the diagnosis.
 *
 * Upstream also has a button that throws an exception on purpose, to prove the
 * error reporter is wired up. There is no error reporter in this application,
 * so the button would prove that an error does nothing.
 */
export function AdminHealthPage() {
  const { t } = useTranslation()
  const health = useHealth()

  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 p-6">
      <header>
        <h1 className="text-xl font-semibold">{t('admin.healthTitle')}</h1>
        <p className="text-muted-foreground text-sm">{t('admin.healthWhy')}</p>
      </header>

      {health.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : health.data === undefined ? (
        <Alert variant="destructive">
          <AlertTitle>{t('admin.healthUnavailable')}</AlertTitle>
        </Alert>
      ) : (
        <section className="space-y-3 rounded-lg border p-4 bg-background on-surface">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-medium">{t('admin.checks')}</h2>
            <Badge variant={health.data.passing ? 'secondary' : 'destructive'}>
              {health.data.passing ? t('admin.passing') : t('admin.failing')}
            </Badge>
          </div>

          <ul className="divide-y">
            {Object.entries(health.data.checks).map(([name, status]) => (
              <li key={name} className="flex items-start justify-between gap-4 py-2">
                <span className="text-sm">{readable(name)}</span>
                {status === WORKING ? (
                  <Check className="text-muted-foreground size-4 shrink-0" />
                ) : (
                  <span className="flex items-start gap-2 text-right">
                    <span className="text-destructive text-sm">{status}</span>
                    <X className="text-destructive size-4 shrink-0" />
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <SystemHealth />

      {health.data !== undefined && <Usage held={health.data.usage} />}

      <EmailTester />
    </div>
  )
}

/**
 * What this instance is holding.
 *
 * On the health page because that is where operational facts about the whole
 * instance belong, and because there is one workspace -- the tenant's -- so a
 * per-workspace breakdown would be a table with one row in it.
 *
 * The two figures are kept by different machinery and go stale differently, so
 * each says how it was arrived at rather than presenting one number. Rows are
 * current; storage is whatever the half-hourly job last wrote, which is nothing
 * at all while "track workspace usage" is off.
 */
function Usage({ held }: { held: InstanceUsage }) {
  const { t } = useTranslation()

  return (
    <section className="space-y-3 rounded-lg border p-4 bg-background on-surface">
      <h2 className="font-medium">{t('admin.usage')}</h2>

      <ul className="divide-y">
        <li className="flex items-center justify-between gap-4 py-2">
          <span className="text-sm">{t('admin.usageRows')}</span>
          <span className="text-sm tabular-nums">{COUNT.format(held.row_count)}</span>
        </li>
        <li className="flex items-center justify-between gap-4 py-2">
          <span className="text-sm">{t('admin.usageStorage')}</span>
          {/* Null is not zero: nothing counted and nothing stored are different
              answers, and a "0 MB" would read as the second. */}
          <span className="text-sm tabular-nums">
            {held.storage_usage_megabytes === null
              ? t('admin.usageNeverCounted')
              : t('admin.usageMegabytes', {
                  size: COUNT.format(held.storage_usage_megabytes),
                })}
          </span>
        </li>
        {held.counted_at !== null && (
          <li className="flex items-center justify-between gap-4 py-2">
            <span className="text-sm">{t('admin.usageCountedAt')}</span>
            <span className="text-muted-foreground text-sm">
              {COUNTED.format(new Date(held.counted_at))}
            </span>
          </li>
        )}
      </ul>

      {!held.tracking && (
        <p className="text-muted-foreground text-sm">{t('admin.usageNotTracked')}</p>
      )}
    </section>
  )
}

/**
 * Whether mail leaves this instance.
 *
 * The one place in the application where a stack trace belongs on screen. The
 * endpoint answers 200 with the failure inside it, precisely so that whoever is
 * debugging an SMTP configuration can read what went wrong instead of a
 * sanitised message.
 */
function EmailTester() {
  const { t } = useTranslation()
  const [address, setAddress] = useState('')
  const test = useEmailTest()
  const answer = test.data

  return (
    <section className="space-y-4 rounded-lg border p-4 bg-background on-surface">
      <div>
        <h2 className="font-medium">{t('admin.emailTest')}</h2>
        <p className="text-muted-foreground text-sm">{t('admin.emailTestWhy')}</p>
      </div>

      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          test.mutate(address)
        }}
      >
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="email-test-address">{t('admin.emailTestAddress')}</Label>
          <Input
            id="email-test-address"
            type="email"
            value={address}
            onChange={(event) => {
              setAddress(event.target.value)
            }}
            className="bg-background on-surface"
          />
        </div>
        <Button type="submit" disabled={address === '' || test.isPending}>
          {t('admin.emailTestSend')}
        </Button>
      </form>

      {answer !== undefined &&
        (answer.succeeded ? (
          <Alert>
            <AlertTitle>{t('admin.emailTestSent')}</AlertTitle>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <AlertTitle>{answer.error ?? t('admin.emailTestFailed')}</AlertTitle>
            {answer.error_stack !== null && answer.error_stack !== undefined && (
              <AlertDescription>
                <pre className="max-h-64 w-full overflow-auto text-xs whitespace-pre-wrap">
                  {answer.error_stack}
                </pre>
              </AlertDescription>
            )}
          </Alert>
        ))}
    </section>
  )
}

/**
 * `redis` stays `redis`; `celeryDefaultQueue` becomes `celery default queue`.
 *
 * The keys are the backend's own attribute names and are shown as they are,
 * spaced out. Translating them would mean a table of every check any deployment
 * might register, which is not a list this application can hold.
 */
function readable(name: string): string {
  return name.replace(/([A-Z])/g, ' $1')
}
